'use client';

import { postCart, useCartStore } from '@/features/cart/store/cart';
import { type CartForm } from '@/features/cart/types';
import { type Country, countryList } from '@/utils/countryList';
import { emailBlockList } from '@/utils/emailBlockList';
import { handleException } from '@/utils/exception';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Checkbox } from '@andritzot/metris-web-ui/inputs/checkbox';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { ToggleButton, ToggleButtonGroup } from '@andritzot/metris-web-ui/inputs/toggle-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { useRouter } from 'next/navigation';
import { Controller, type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

const contactOptions = [
  { label: 'Send information\nand contact me', value: true },
  { label: 'Send information only', value: false },
];

interface FormProps extends CartForm {
  comment: string;
  country: Country | null;
  city: string;
  agreePrivacy: boolean;
}

const FIELD_BACKGROUND = 'background.paper';

const validateEmail = (value: string): boolean | string => {
  if (emailBlockList.some((suffix) => value.endsWith(suffix))) return 'Please enter a valid business email address';
  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) return 'Invalid email address';
  return true;
};

const PriceQuoteForm = () => {
  const { control, handleSubmit, reset, getValues } = useForm<FormProps>({
    defaultValues: {
      beContacted: true,
      comment: '',
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      jobTitle: '',
      country: null,
      city: '',
      phone: '',
      agreePrivacy: false,
    },
  });
  const agreePrivacy = useWatch({ control, name: 'agreePrivacy' });
  const router = useRouter();
  const cartProducts = useCartStore((s) => s.cartProducts);
  const isLoading = useCartStore((s) => s.isLoading);

  const isCartEmpty = cartProducts.length === 0;

  const onSubmit: SubmitHandler<FormProps> = async (data) => {
    const { city, company, beContacted, country, email, firstName, lastName, jobTitle, phone, comment } = data;
    try {
      await postCart({
        city,
        beContacted,
        company,
        email,
        firstName,
        lastName,
        jobTitle,
        phone,
        country: country?.name ?? '',
        comment,
      });
      toast.success('Request has been sent!', {
        autoClose: false,
      });
      reset();
    } catch (e) {
      toast.error(handleException(e, 'Error sending data to server'), {
        autoClose: false,
      });
    }
  };

  return (
    <Box sx={{ margin: '20px' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {!isCartEmpty && (
          <Controller
            name="beContacted"
            control={control}
            render={({ field }) => (
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  exclusive
                  value={field.value}
                  onChange={(_, val) => val !== null && field.onChange(val)}
                  sx={{ width: '100%' }}
                >
                  {contactOptions.map((opt) => (
                    <ToggleButton
                      key={String(opt.value)}
                      value={opt.value}
                      sx={{
                        flex: 1,
                        whiteSpace: 'pre-line',
                        minHeight: '64px',
                        py: 2,
                        px: 3,
                        backgroundColor: FIELD_BACKGROUND,
                        '&.Mui-selected': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                        '&.Mui-selected:focus': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                        '&.Mui-selected:hover': { backgroundColor: 'primary.dark', color: 'primary.contrastText' },
                      }}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}
          />
        )}

        <Controller
          name="firstName"
          control={control}
          rules={{ required: 'required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="* First Name"
              fullWidth
              autoComplete="given-name"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{ required: 'required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="* Surname"
              fullWidth
              autoComplete="family-name"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          rules={{
            required: 'required',
            validate: () => validateEmail(getValues('email')),
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="* Email"
              type="email"
              fullWidth
              autoComplete="email"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="company"
          control={control}
          rules={{ required: 'required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="* Company"
              fullWidth
              autoComplete="organization"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="jobTitle"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Job Title"
              fullWidth
              autoComplete="organization-title"
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="country"
          control={control}
          rules={{ required: 'required' }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete<Country, false, false, false>
              options={countryList}
              getOptionLabel={(opt) => opt.name}
              value={field.value}
              onChange={(_, val) => field.onChange(val)}
              isOptionEqualToValue={(opt, val) => opt.name === val.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="* Country"
                  error={!!error}
                  helperText={error?.message}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: FIELD_BACKGROUND } }}
                />
              )}
              sx={{ mb: 1.5 }}
            />
          )}
        />

        <Controller
          name="city"
          control={control}
          rules={{ required: 'required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="* City"
              fullWidth
              autoComplete="home city"
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field: { onChange, ...field } }) => (
            <TextField
              {...field}
              label="Phone"
              fullWidth
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              onChange={(e) => onChange(e.target.value.replace(/\D+/g, ''))}
              sx={{ mb: 1.5, '& .MuiInputBase-input': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="comment"
          rules={{ required: isCartEmpty ? 'required' : false }}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={isCartEmpty ? '* Tell us what you are looking for' : 'Additional information'}
              fullWidth
              multiline
              rows={3}
              error={!!error}
              helperText={error?.message}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: FIELD_BACKGROUND } }}
            />
          )}
        />

        <Controller
          name="agreePrivacy"
          control={control}
          rules={{ required: 'required' }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} sx={{ p: 0 }} />
                <Typography variant="body2" sx={{ color: error ? 'error.main' : 'text.primary' }}>
                  I have read and agree to the{' '}
                  <a href="https://www.andritz.com/privacy" target="_blank" rel="noreferrer">
                    ANDRITZ data privacy declaration
                  </a>
                  {'. '}I can revoke this consent at any time.
                </Typography>
              </Box>
              {error && (
                <Typography variant="caption" sx={{ color: 'error.main', ml: 0.5 }}>
                  required
                </Typography>
              )}
            </Box>
          )}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', my: '10px' }}>
            <Button type="submit" variant="contained" disabled={!agreePrivacy || isLoading} size="large">
              Request information
            </Button>
          </Box>
          <Button
            variant="text"
            size="large"
            sx={{ color: 'primary.main', my: '10px' }}
            onClick={() => router.push('/')}
          >
            Search more solutions
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PriceQuoteForm;
